import { useMemo, useRef, useState } from 'react'
import {
  LayoutContextProvider,
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useCreateLayoutContext,
  useRemoteParticipants,
  useRoomContext,
} from '@livekit/components-react'
import { ConnectionState } from 'livekit-client'
import type { MediaDeviceFailure, RoomOptions, VideoCaptureOptions } from 'livekit-client'
import { ShadcnScope } from '@/custolink-newdesign/components/ShadcnScope'
import { Button } from '@/custolink-newdesign/ui/button'
import { cn } from '@/custolink-newdesign/lib/utils'
import {
  AutoEnableTracks,
  mediaFailureMessage,
  useVideoRoomConnection,
} from '@/custolink-views/shared/useVideoRoomConnection'
import type { VideoRoomEndedInfo } from '@/custolink-views/shared/useVideoRoomConnection'
import { RoomSyncProvider, useRoomSyncOptional } from '@/custolink-newdesign/remote-support-sync/RoomSyncProvider'
import { StageSyncLayer } from '@/custolink-newdesign/remote-support-sync/StageSyncLayer'
import { guestSyncApi } from '@/custolink-newdesign/remote-support-sync/syncApi'
import { GUEST_PUBLISH_DEFAULTS } from '@/custolink-views/shared/guestPublishDefaults'
import { tokenCanPublish } from '@/custolink-views/shared/recording_consent/recordingStatus'
import {
  guestRecordingConsentApi,
  type RecordingConsentApi,
} from '@/custolink-views/shared/recording_consent/recordingConsentApi'
import { useRecordingConsent } from '@/custolink-views/shared/recording_consent/useRecordingConsent'
import { RoomHeader } from './RoomHeader'
import { RoomBlockingCard } from './RoomBlockingCard'
import { RecordingConsentCard } from './RecordingConsentCard'
import { EquipmentStage } from './EquipmentStage'
import { CallBar } from './CallBar'

/**
 * Rear camera by default (D-05): a guest joining from a phone is showing equipment to the
 * expert, not their own face. Declared at module scope - LiveKitRoom re-runs its connect effect
 * whenever `video`/`options` change identity, so an inline object here would restart the local
 * camera track on every render (T-04-43).
 * publishDefaults is GUEST_PUBLISH_DEFAULTS (R-03): this is a guest room, so its camera publishes
 * H264 with no backup codec and no simulcast, same as every other guest room.
 */
const TECHNICIAN_VIDEO_CAPTURE: VideoCaptureOptions = { facingMode: 'environment' }
const TECHNICIAN_ROOM_OPTIONS: RoomOptions = {
  videoCaptureDefaults: TECHNICIAN_VIDEO_CAPTURE,
  publishDefaults: GUEST_PUBLISH_DEFAULTS,
}

/**
 * Mounted inside LiveKitRoom and LayoutContextProvider - reads the room via useRoomContext() for
 * Leave (a plain disconnect, never an API call, D-01) and the connection state for the elapsed
 * time anchor.
 *
 * Layout: a grid that is a single column (header / stage / call bar) in portrait and on any
 * desktop or tablet window, and a two-column grid only on a phone held sideways
 * (`phone-landscape`), where the stage spans the full height on the left and the header/call
 * bar sit in a narrow 88px right-hand column - header pinned to the top of that column, call
 * bar pinned to the bottom, so the stage never loses height to the controls (D-05).
 */
function TechnicianBody(p: {
  title: string
  subtitle?: string
  recording: boolean
  consentApi: RecordingConsentApi | null
  token: string
  secondaryAction?: { label: string; onClick: () => void }
  onMediaFailure: (failure: MediaDeviceFailure) => void
}) {
  const room = useRoomContext()
  const sync = useRoomSyncOptional()
  const connectionState = useConnectionState()
  const remoteParticipants = useRemoteParticipants()
  const [thumbnailsCollapsed, setThumbnailsCollapsed] = useState(false)

  // D-05/D-09/D-06 for the guest: the roomId-scoped public consent API only, so no worker surface
  // ever reaches this composition (T-06.1-48). Null (no roomId) means no consent surface at all.
  const consent = useRecordingConsent({
    api: p.consentApi,
    token: p.token,
    role: 'customer',
    onMediaFailure: p.onMediaFailure,
  })

  // First moment both sides are connected, kept in a ref so a later reconnect
  // (SignalReconnecting -> Connected again) never resets the elapsed-time anchor.
  const anchorRef = useRef<number | null>(null)
  if (
    anchorRef.current == null &&
    connectionState === ConnectionState.Connected &&
    remoteParticipants.length > 0
  ) {
    anchorRef.current = Date.now()
  }

  const toggleThumbnails = () => setThumbnailsCollapsed((v) => !v)

  return (
    <div
      className={cn(
        'cl:grid cl:min-h-0 cl:flex-1 cl:overflow-hidden',
        'cl:grid-cols-1 cl:grid-rows-[auto_1fr_auto]',
        'cl:phone-landscape:grid-cols-[1fr_88px] cl:phone-landscape:grid-rows-[auto_1fr]',
      )}
    >
      <div className="cl:row-start-1 cl:phone-landscape:col-start-2 cl:phone-landscape:row-start-1 cl:phone-landscape:pr-[env(safe-area-inset-right)]">
        <RoomHeader
          compact
          title={p.title}
          subtitle={p.subtitle}
          elapsedAnchorMs={anchorRef.current}
          recording={consent ? consent.ui.capturing : p.recording}
          stopRecordingDisabled={consent?.deciding}
          trailing={
            p.secondaryAction != null ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="cl:min-h-[44px] cl:min-w-[44px] cl:bg-black/50 cl:text-white cl:border-white/20 cl:hover:bg-black/70 cl:hover:text-white"
                onClick={p.secondaryAction.onClick}
              >
                {p.secondaryAction.label}
              </Button>
            ) : undefined
          }
          onStopRecording={
            consent && consent.ui.capturing && consent.ui.canDecline
              ? () => consent.decide(false)
              : undefined
          }
        />
      </div>

      <div className="cl:relative cl:row-start-2 cl:min-h-0 cl:min-w-0 cl:phone-landscape:col-start-1 cl:phone-landscape:row-start-1 cl:phone-landscape:row-span-2">
        <EquipmentStage
          role="customer"
          layout="mobile"
          waitingLabel="support"
          thumbnailsCollapsed={thumbnailsCollapsed}
          renderStageOverlay={
            sync != null ? (t) => <StageSyncLayer layout="mobile" stageTrack={t} /> : undefined
          }
          followTrackSid={sync?.room?.activeSource?.trackSid ?? null}
          onToggleThumbnails={toggleThumbnails}
        />
        {consent && (
          <RecordingConsentCard
            suppressRecordingPill
            consent={consent}
            layout="mobile"
            lifted={(sync?.room?.activeFrame ?? null) != null}
          />
        )}
      </div>

      <div className="cl:row-start-3 cl:phone-landscape:col-start-2 cl:phone-landscape:row-start-2 cl:phone-landscape:self-end cl:phone-landscape:w-full cl:phone-landscape:pr-[env(safe-area-inset-right)]">
        <CallBar
          layout="mobile"
          thumbnailsCollapsed={thumbnailsCollapsed}
          onLeave={() => room.disconnect()}
          onToggleThumbnails={toggleThumbnails}
        />
      </div>
    </div>
  )
}

/**
 * The guest-side mobile technician room (D-05): opens on the rear camera with Flip camera
 * available, fits a phone in portrait and landscape with every control at least 44x44px, and has
 * no worker-only surface at all (D-06) - no orgId/callId, no side panel, and CallBar is never
 * given canEndSession/onEndSession, so no End session control ever renders.
 *
 * Only the in-room view: the pre-join, "you have left" (rejoin) and "call has ended" screens of
 * both Phase 3 guest flows (VideoByLink.tsx, VideoCallAppPageComponent.tsx) stay exactly as they
 * were. Keeps Phase 3's connection semantics via useVideoRoomConnection - Leave is always a
 * LiveKit disconnect with no API call, and the recording notice is driven by the server's
 * `recording` flag, passed straight through (T-04-42).
 */
export function MobileTechnicianRoom(p: {
  token: string
  serverUrl: string
  recording: boolean
  title: string
  subtitle?: string
  embedded?: boolean
  secondaryAction?: { label: string; onClick: () => void }
  /** A guest capability, not a worker prop (D-06): runs the sync core through the roomId-scoped public API. */
  roomId?: string
  onEnded: (info: VideoRoomEndedInfo) => void
}) {
  const conn = useVideoRoomConnection({ onEnded: p.onEnded })
  const layoutContext = useCreateLayoutContext()

  const syncApi = useMemo(() => (p.roomId ? guestSyncApi(p.roomId) : null), [p.roomId])

  // The guest reaches only the roomId-scoped public consent API. A token that withholds publishing
  // (D-06) keeps the camera and microphone down until the server grants publish; TECHNICIAN_ROOM_OPTIONS
  // still makes the camera that comes up the rear one publishing H264.
  const consentApi = useMemo(
    () => (p.roomId ? guestRecordingConsentApi(p.roomId) : null),
    [p.roomId],
  )
  const held = useMemo(() => !tokenCanPublish(p.token), [p.token])

  if (!p.token || !p.serverUrl) {
    return (
      <ShadcnScope
        className={cn(
          'cl:relative cl:flex cl:items-center cl:justify-center cl:bg-black cl:text-white',
          p.embedded ? 'cl:h-full' : 'cl:h-dvh',
        )}
      >
        <RoomBlockingCard title="Missing video credentials">
          This link is incomplete. Please open the invite link again.
        </RoomBlockingCard>
      </ShadcnScope>
    )
  }

  const media = conn.mediaFailure != null ? mediaFailureMessage(conn.mediaFailure) : null

  const technicianBody = (
    <TechnicianBody
      title={p.title}
      subtitle={p.subtitle}
      recording={p.recording}
      consentApi={consentApi}
      token={p.token}
      secondaryAction={p.secondaryAction}
      onMediaFailure={conn.reportMediaFailure}
    />
  )

  return (
    <ShadcnScope
      className={cn(
        'cl:relative cl:flex cl:flex-col cl:overflow-hidden cl:bg-black cl:text-white',
        p.embedded ? 'cl:h-full' : 'cl:h-dvh',
      )}
    >
      <LiveKitRoom
        key={conn.attempt}
        token={p.token}
        serverUrl={p.serverUrl}
        connect={true}
        audio={!held}
        video={!held}
        options={TECHNICIAN_ROOM_OPTIONS}
        className="cl:flex cl:min-h-0 cl:flex-1 cl:flex-col cl:phone-landscape:flex-row"
        onConnected={conn.handleConnected}
        onDisconnected={conn.handleDisconnected}
        onError={conn.handleError}
        onMediaDeviceFailure={conn.handleMediaDeviceFailure}
      >
        <AutoEnableTracks enabled={!held} onFailure={conn.reportMediaFailure} />
        <LayoutContextProvider value={layoutContext}>
          {syncApi != null ? (
            <RoomSyncProvider api={syncApi}>{technicianBody}</RoomSyncProvider>
          ) : (
            technicianBody
          )}
        </LayoutContextProvider>
        <RoomAudioRenderer />
      </LiveKitRoom>

      {media != null && (
        <RoomBlockingCard title={media.title} actionLabel="Try again" onAction={conn.retry}>
          {media.body}
        </RoomBlockingCard>
      )}

      {media == null && conn.roomError != null && (
        <RoomBlockingCard title="Could not connect" actionLabel="Try again" onAction={conn.retry}>
          {conn.roomError.message ||
            'Something went wrong while connecting to the video call.'}
        </RoomBlockingCard>
      )}

      {media == null && conn.roomError == null && conn.failedBeforeConnecting && (
        <RoomBlockingCard title="Could not connect" actionLabel="Try again" onAction={conn.retry}>
          The video service closed the connection before the call started. Check that the video
          server is reachable from this device.
        </RoomBlockingCard>
      )}
    </ShadcnScope>
  )
}

export default MobileTechnicianRoom
